import { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  openTutorialModal,
  closeTutorialModal,
  setTutorialStep,
  setTutorialCompleted,
  skipTutorial,
} from '../store/actions/dashboardActions'
import { getTutorialRef } from '../utils/tutorialRefsRegistry.ts'

export type ActionTab = 'obras' | 'materiales' | 'personas' | 'marketplace' | 'documentacion' | 'admin'

export interface TutorialStep {
  id: string
  targetSelector?: string
  title?: string
  description: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  actionId?: string
  requiredTab?: ActionTab
  isIntro?: boolean
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'intro',
    title: 'Recorrido guiado',
    description: 'Te mostramos las funciones clave de Edify en unos pasos. Podés avanzar cuando quieras o saltar el recorrido.',
    position: 'center',
    isIntro: true,
    targetSelector: '[data-tutorial="tutorial-intro-fallback"]',
  },
  {
    id: 'tabs',
    targetSelector: '[data-tutorial="action-tabs"]',
    title: 'Explora las categorías',
    description: 'Elegí la categoría que quieras explorar: Obras, Materiales, Personas o Marketplace.',
    position: 'bottom',
  },
  {
    id: 'presupuestos-obra',
    targetSelector: '[data-tutorial="action-presupuestos-obra"]',
    title: 'Solicita presupuestos',
    description: 'Creá una solicitud de obra privada y recibí cotizaciones de profesionales verificados.',
    position: 'right',
    actionId: 'presupuestos-obra',
    requiredTab: 'obras',
  },
  {
    id: 'cotizar-obras',
    targetSelector: '[data-tutorial="action-cotizar-obras"]',
    title: 'Cotiza en obras',
    description: 'Revisá obras abiertas y presentá tu cotización para sumar nuevos trabajos.',
    position: 'left',
    actionId: 'cotizar-obras',
    requiredTab: 'obras',
  },
  {
    id: 'cotizar-materiales',
    targetSelector: '[data-tutorial="action-cotizar-materiales"]',
    title: 'Pedir una cotización',
    description: 'Pedí precios de materiales en simultáneo a múltiples proveedores.',
    position: 'right',
    actionId: 'cotizar-materiales',
    requiredTab: 'materiales',
  },
  {
    id: 'estimacion-costos',
    targetSelector: '[data-tutorial="action-estimacion-costos"]',
    title: 'Estimación de costos',
    description: 'Consultá costos actualizados para estimar rápidamente el presupuesto de tu obra.',
    position: 'left',
    actionId: 'estimacion-costos',
    requiredTab: 'materiales',
  },
  {
    id: 'encontrar-profesionales',
    targetSelector: '[data-tutorial="action-encontrar-profesionales"]',
    title: 'Explorar perfiles',
    description: 'Buscá y contactá arquitectos, ingenieros, trabajadores de obra u otros especialistas para tu proyecto.',
    position: 'right',
    actionId: 'encontrar-profesionales',
    requiredTab: 'personas',
  },
  {
    id: 'postularme-ofertas',
    targetSelector: '[data-tutorial="action-postularme-ofertas"]',
    title: 'Postularme a ofertas de trabajo',
    description: 'Aplicá a las ofertas de trabajo disponibles y sumate a proyectos.',
    position: 'left',
    actionId: 'postularme-ofertas',
    requiredTab: 'personas',
  },
  {
    id: 'explorar-servicios',
    targetSelector: '[data-tutorial="action-explorar-servicios"]',
    title: 'Explorar servicios/productos',
    description: 'Descubrí servicios y productos publicados por otros profesionales.',
    position: 'right',
    actionId: 'explorar-servicios',
    requiredTab: 'marketplace',
  },
  {
    id: 'ofrecer-servicios',
    targetSelector: '[data-tutorial="action-ofrecer-servicios"]',
    title: 'Ofrecer servicios/productos',
    description: 'Publicá tus servicios o productos para llegar a nuevos clientes.',
    position: 'left',
    actionId: 'ofrecer-servicios',
    requiredTab: 'marketplace',
  },
]

const STORAGE_KEY = 'edify_tutorial_last_shown'
const DEFAULT_TUTORIAL_STATE = {
  isActive: false,
  currentStep: 0,
  completed: false,
  skipped: false,
  lastShownDate: null,
}

export const useTutorialSpotlight = () => {
  const dispatch = useDispatch()
  const tutorialState = useSelector((state: any) => state.dashboard?.tutorial ?? DEFAULT_TUTORIAL_STATE)
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)

  const shouldShowTutorial = useCallback(() => {
    if (!tutorialState) {
      return false
    }

    const lastShownDate = localStorage.getItem(STORAGE_KEY)
    const today = new Date().toDateString()
    
    const isCompleted = tutorialState?.completed ?? false
    const isSkipped = tutorialState?.skipped ?? false
    
    if (isCompleted || isSkipped) {
      return false
    }
    
    if (tutorialState?.lastShownDate) {
      return tutorialState.lastShownDate !== today
    }
    
    return !lastShownDate || lastShownDate !== today
  }, [tutorialState])

  const startTutorial = useCallback(() => {
    if (!shouldShowTutorial()) return
    
    dispatch(openTutorialModal())
    localStorage.setItem(STORAGE_KEY, new Date().toDateString())
  }, [dispatch, shouldShowTutorial])

  const completeTutorial = useCallback(() => {
    dispatch(setTutorialCompleted())
    localStorage.setItem(STORAGE_KEY, new Date().toDateString())
  }, [dispatch])

  const nextStep = useCallback(() => {
    const currentStep = tutorialState.currentStep
    const totalSteps = TUTORIAL_STEPS.length
    
    if (currentStep < totalSteps - 1) {
      dispatch(setTutorialStep(currentStep + 1))
    } else {
      completeTutorial()
    }
  }, [dispatch, tutorialState.currentStep, completeTutorial])

  const prevStep = useCallback(() => {
    const currentStep = tutorialState.currentStep
    if (currentStep > 0) {
      dispatch(setTutorialStep(currentStep - 1))
    }
  }, [dispatch, tutorialState.currentStep])

  const skipTutorialHandler = useCallback(() => {
    dispatch(skipTutorial())
    localStorage.setItem(STORAGE_KEY, new Date().toDateString())
  }, [dispatch])

  const closeTutorial = useCallback(() => {
    dispatch(closeTutorialModal())
  }, [dispatch])

  const getCurrentStep = useCallback((): TutorialStep | null => {
    if (!tutorialState.isActive) return null
    return TUTORIAL_STEPS[tutorialState.currentStep] || null
  }, [tutorialState.isActive, tutorialState.currentStep])

  const changeTabIfNeeded = useCallback((step: TutorialStep) => {
    if (step.requiredTab) {
      const event = new CustomEvent('tutorial-change-tab', {
        detail: { tab: step.requiredTab }
      })
      window.dispatchEvent(event)
      
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 300)
      })
    }
    return Promise.resolve()
  }, [])

  const updateTargetElement = useCallback(async () => {
    if (!tutorialState.isActive) {
      setTargetElement(null)
      setSpotlightRect(null)
      return
    }

    const currentStep = getCurrentStep()
    if (!currentStep) {
      setTargetElement(null)
      setSpotlightRect(null)
      return
    }

    await changeTabIfNeeded(currentStep)

    if (!currentStep.targetSelector) {
      setTargetElement(null)
      setSpotlightRect(null)
      return
    }

    const elementId = currentStep.targetSelector.replace('[data-tutorial="', '').replace('"]', '')
    const element = getTutorialRef(elementId)
    
    if (element) {
      setTargetElement(element)
      const rect = element.getBoundingClientRect()
      setSpotlightRect(rect)
      
      requestAnimationFrame(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      })
    } else {
      setTargetElement(null)
      setSpotlightRect(null)
    }
  }, [tutorialState.isActive, getCurrentStep, changeTabIfNeeded])

  useEffect(() => {
    if (tutorialState.isActive) {
      updateTargetElement()
      
      const handleResize = () => {
        updateTargetElement()
      }
      
        window.addEventListener('resize', handleResize)
        const observerInstance = observerRef.current
        
        return () => {
          window.removeEventListener('resize', handleResize)
          if (observerInstance) {
            observerInstance.disconnect()
          }
        }
    } else {
      setTargetElement(null)
      setSpotlightRect(null)
    }
  }, [tutorialState.isActive, tutorialState.currentStep, updateTargetElement])

  return {
    isActive: tutorialState.isActive,
    currentStep: tutorialState.currentStep,
    totalSteps: TUTORIAL_STEPS.length,
    currentStepData: getCurrentStep(),
    targetElement,
    spotlightRect,
    startTutorial,
    nextStep,
    prevStep,
    completeTutorial,
    skipTutorial: skipTutorialHandler,
    closeTutorial,
    shouldShowTutorial: shouldShowTutorial(),
    steps: TUTORIAL_STEPS,
  }
}

